import {
  AutopilotTaskDto,
  useInvalidateUseGetAutopilotTasksForCampaign,
} from '@urbanx/agx.activities.client';
import { useToast } from '@urbanx/agx.ui-components2';
import { AuthContext } from '@urbanx/agx.auth.client';
import { DocumentTypes, UploadedDocument } from '@urbanx/agx.campaigns.client';
import { AxiosProgressEvent, GenericAbortSignal } from 'axios';
import { useCallback, useContext, useEffect, useState } from 'react';
import { uploadFile } from 'services';
import { getExchangeTaskFamily } from 'utils/tasks/exchangeTaskIdentity';
interface UseMarkedAsExchangeProps {
  task: AutopilotTaskDto;
}

export const useMarkedAsExchange = ({ task }: UseMarkedAsExchangeProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<string | undefined>();
  const [exchangeLetterFiles, setExchangeLetterFiles] = useState<(UploadedDocument | File)[]>([]);
  const [signedContractFiles, setSignedContractFiles] = useState<(UploadedDocument | File)[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { getAuthToken } = useContext(AuthContext);
  const { pushToast } = useToast();
  const { campaignId, contractId } = task;
  const exchangeFamily = getExchangeTaskFamily(task.category?.taskType);
  const isAgentExchange = exchangeFamily === 'agent' || exchangeFamily === 'contract';
  const isSolicitorExchange = exchangeFamily === 'solicitor';
  const invalidateTasks = useInvalidateUseGetAutopilotTasksForCampaign();

  const openModal = () => {
    if (task.isLocked) return;
    setIsOpen(true);
  };
  const closeModal = () => {
    setIsOpen(false);
  };

  const resetState = useCallback(() => {
    setSelectedOption(undefined);
    setExchangeLetterFiles([]);
    setSignedContractFiles([]);
    setIsSubmitting(false);
  }, []);

  const buildRenamedFormData = useCallback(
    (formData: FormData) => {
      const file = formData.get('File');
      if (!file || typeof File === 'undefined' || !(file instanceof File)) {
        return formData;
      }

      const renamedFile = new File([file], `${contractId}/${file.name}`, {
        type: file.type,
        lastModified: file.lastModified,
      });
      const renamedFormData = new FormData();
      renamedFormData.append('File', renamedFile);
      return renamedFormData;
    },
    [contractId]
  );

  const onUploadFile = useCallback(
    async (
      authToken: string,
      file: string | FormData,
      uploadCampaignId: string,
      uploadedFormType: DocumentTypes,
      uploadProgress?: (progressEvent: AxiosProgressEvent) => void,
      abortSignal?: GenericAbortSignal,
      uploadContractId?: string
    ) => {
      const renamedFormData =
        typeof FormData !== 'undefined' && file instanceof FormData
          ? buildRenamedFormData(file)
          : file;

      return await uploadFile(
        authToken,
        renamedFormData,
        uploadCampaignId,
        uploadedFormType,
        uploadProgress ?? null,
        abortSignal ?? null,
        undefined,
        uploadContractId
      );
    },
    [buildRenamedFormData]
  );

  useEffect(() => {
    if (!isOpen) {
      setSelectedOption(undefined);
      resetState();
    }
  }, [isOpen, resetState]);

  const onExchangeLetterChanged = useCallback(
    ({ value }: { value?: (UploadedDocument | File)[] }) => {
      setExchangeLetterFiles(value ?? []);
    },
    []
  );

  const onSignedContractChanged = useCallback(
    ({ value }: { value?: (UploadedDocument | File)[] }) => {
      setSignedContractFiles(value ?? []);
    },
    []
  );

  const uploadSelectedFile = useCallback(
    async (
      authToken: string,
      uploadCampaignId: string,
      documentType: DocumentTypes,
      selectedFile?: UploadedDocument | File,
      uploadContractId?: string
    ) => {
      if (!selectedFile || !(selectedFile instanceof File)) return selectedFile;

      const formData = new FormData();
      formData.append('File', selectedFile);

      return onUploadFile(
        authToken,
        formData,
        uploadCampaignId,
        documentType,
        undefined,
        undefined,
        uploadContractId
      );
    },
    [onUploadFile]
  );

  const onConfirm = async () => {
    if (task.isLocked) return;
    if (!campaignId) return;
    if (isSolicitorExchange && exchangeLetterFiles.length === 0) return;
    if (isAgentExchange && signedContractFiles.length === 0) return;
    if (isSolicitorExchange && selectedOption === 'Yes' && signedContractFiles.length === 0) {
      return;
    }

    const authToken = await getAuthToken();
    if (!authToken) return;

    setIsSubmitting(true);

    try {
      await uploadSelectedFile(
        authToken,
        campaignId,
        isAgentExchange ? DocumentTypes.SignedContract : DocumentTypes.ExchangeLetter,
        isAgentExchange ? signedContractFiles[0] : exchangeLetterFiles[0],
        contractId
      );

      if (isSolicitorExchange && selectedOption === 'Yes') {
        await uploadSelectedFile(
          authToken,
          campaignId,
          DocumentTypes.SignedContract,
          signedContractFiles[0],
          contractId
        );
      }

      await invalidateTasks(campaignId, contractId);
      closeModal();
    } catch (error) {
      pushToast({
        type: 'error',
        message:
          error instanceof Error
            ? error.message
            : 'The exchange documents could not be uploaded. Check the file and try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isConfirmDisabled =
    task.isLocked === true ||
    (isSolicitorExchange && !selectedOption) ||
    (isSolicitorExchange && exchangeLetterFiles.length === 0) ||
    (isAgentExchange && signedContractFiles.length === 0) ||
    (isSolicitorExchange && selectedOption === 'Yes' && signedContractFiles.length === 0) ||
    isSubmitting;

  return {
    campaignId,
    exchangeLetterFiles,
    getAuthToken,
    isConfirmDisabled,
    isOpen,
    openModal,
    closeModal,
    selectedOption,
    isAgentExchange,
    isSolicitorExchange,
    setIsDialogOpen: setIsOpen,
    setSelectedOption,
    onConfirm,
    onExchangeLetterChanged,
    onSignedContractChanged,
    signedContractFiles,
    onUploadFile,
  };
};
